import { useRef } from "react";
import { toast } from "sonner";
import { UploadCloud, Camera, ClipboardPaste, X } from "lucide-react";
import { EVIDENCE_TYPES } from "@/lib/api";

const OK = ["image/jpeg", "image/png", "image/webp"];

export function EvidenceDrop({ label, hint, emptyTitle, files, setFiles, defaultType, testid }) {
  const input = useRef(null);
  const add = (list) => {
    const arr = Array.from(list);
    const bad = arr.filter((x) => !OK.includes(x.type) || x.size > 10 * 1024 * 1024);
    if (bad.length) toast.error("Use imagens JPG, PNG ou WEBP de até 10MB.");
    const ok = arr.filter((x) => !bad.includes(x)).map((file) => ({ file, url: URL.createObjectURL(file), type: defaultType }));
    setFiles((p) => { const n = [...p, ...ok]; if (n.length > 6) toast.error("Máximo de 6 anexos neste grupo."); return n.slice(0, 6); });
  };
  const paste = async () => {
    try {
      const items = await navigator.clipboard.read();
      const blobs = [];
      for (const it of items) for (const t of it.types) if (OK.includes(t)) blobs.push(new File([await it.getType(t)], `print-${Date.now()}.png`, { type: t }));
      blobs.length ? add(blobs) : toast.error("Nenhuma imagem na área de transferência.");
    } catch { toast.info("Pressione Ctrl+V (ou Cmd+V) sobre a área de anexos para colar o print."); }
  };
  const onPaste = (e) => { const f = Array.from(e.clipboardData?.files || []); if (f.length) { e.preventDefault(); add(f); } };

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between"><p className="text-sm font-semibold text-slate-900">{label}</p><span className="text-[11px] text-slate-500" data-testid={`${testid}-count`}>{files.length}/6 anexos</span></div>
      <p className="mt-0.5 text-xs text-slate-500">{hint}</p>
      <div tabIndex={0} onPaste={onPaste} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); add(e.dataTransfer.files); }}
        className="mt-3 flex flex-col items-center rounded-2xl border border-dashed border-slate-300 bg-slate-50/60 px-4 py-7 text-center outline-none transition-colors focus:border-amber-400 hover:border-amber-400">
        <span className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white"><UploadCloud className="h-5 w-5 text-slate-900" /></span>
        <p className="mt-3 text-sm font-semibold text-slate-900">{emptyTitle}</p>
        <p className="mt-0.5 text-xs text-slate-500">JPG, PNG ou WEBP • você também pode colar um print</p>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          <button type="button" data-testid={`${testid}-upload-btn`} onClick={() => input.current.click()} className="inline-flex h-10 items-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-medium text-white hover:bg-slate-800"><Camera className="h-4 w-4" /> Tirar foto / enviar</button>
          <button type="button" data-testid={`${testid}-paste-btn`} onClick={paste} className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-900 hover:bg-slate-100"><ClipboardPaste className="h-4 w-4" /> Colar imagem</button>
        </div>
        <input ref={input} data-testid={`${testid}-input`} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
      </div>
      {files.length > 0 && (
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {files.map((it, i) => (
            <div key={it.url} className="flex gap-2.5 rounded-xl border border-slate-200 bg-white p-2" data-testid={`${testid}-item-${i}`}>
              <img src={it.url} alt="" className="h-14 w-14 rounded-lg object-cover" />
              <div className="min-w-0 flex-1">
                <select data-testid={`${testid}-type-${i}`} value={it.type} onChange={(e) => setFiles(files.map((x, j) => (j === i ? { ...x, type: e.target.value } : x)))} className="h-9 w-full rounded-lg border border-slate-300 bg-white px-2 text-xs">
                  {Object.entries(EVIDENCE_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <p className="mt-1 truncate text-[11px] text-slate-500">{it.file.name}</p>
              </div>
              <button type="button" data-testid={`${testid}-remove-${i}`} onClick={() => setFiles(files.filter((_, j) => j !== i))} className="self-start p-1 text-slate-400 hover:text-red-600"><X className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
